/**
 * Live Issue Tracker Page
 * =======================
 * Track and prioritize live issues for the ruthless fix loop.
 * Enables disciplined issue triage and weekly fix cycles.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Bug, AlertTriangle, AlertCircle, CheckCircle2, Clock, 
  Plus, X, Filter, RefreshCw, Loader2, ChevronRight,
  Target, TrendingUp, Wrench, ArrowUp, ArrowDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Issue areas
const AREAS = [
  { value: 'job_creation', label: 'Job Creation' },
  { value: 'assignment', label: 'Assignment' },
  { value: 'contractor_ui', label: 'Contractor UI' },
  { value: 'uploads', label: 'Uploads' },
  { value: 'quote', label: 'Quote' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'ppm', label: 'PPM' },
  { value: 'reporting', label: 'Reporting' },
  { value: 'routing', label: 'Routing' },
  { value: 'automation', label: 'Automation' },
  { value: 'auth', label: 'Auth' },
  { value: 'notifications', label: 'Notifications' },
  { value: 'portal', label: 'Portal' },
  { value: 'general', label: 'General' },
];

// Issue sources
const SOURCES = [
  { value: 'fm', label: 'FM Staff' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'client', label: 'Client' },
  { value: 'system', label: 'System' },
  { value: 'internal', label: 'Internal' },
];

// Severity levels
const SEVERITIES = [
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800 border-red-200' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-800 border-slate-200' },
];

// Priority badges
const PRIORITY_STYLES = {
  p0: 'bg-red-600 text-white',
  p1: 'bg-orange-500 text-white',
  p2: 'bg-yellow-500 text-slate-900',
  p3: 'bg-slate-400 text-white',
};

// Status badges
const STATUS_STYLES = {
  open: 'bg-red-100 text-red-800',
  triaged: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-purple-100 text-purple-800',
  fixed: 'bg-green-100 text-green-800',
  closed: 'bg-slate-100 text-slate-800',
  wont_fix: 'bg-slate-100 text-slate-600',
};

export default function LiveIssueTrackerPage() {
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState([]);
  const [summary, setSummary] = useState(null);
  const [topFixes, setTopFixes] = useState([]);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  
  // Create issue form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newIssue, setNewIssue] = useState({
    source: 'fm',
    area: 'general',
    severity: 'medium',
    title: '',
    description: '',
    expected_behaviour: '',
    actual_behaviour: '',
    impact: '',
    job_id: '',
    client_id: '',
    contractor_id: ''
  });
  
  // Fetch issues
  const fetchIssues = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterArea) params.append('area', filterArea);
      if (filterSeverity) params.append('severity', filterSeverity);
      if (filterPriority) params.append('fix_priority', filterPriority);
      
      const response = await axios.get(`${API}/api/live-issues?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        setIssues(response.data.issues || []);
      }
    } catch (err) {
      console.error('Failed to fetch issues:', err);
      toast.error('Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterArea, filterSeverity, filterPriority]);
  
  // Fetch summary
  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/live-issues/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        setSummary(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };
  
  // Fetch top fixes
  const fetchTopFixes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/live-issues/top-fixes?limit=3`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        setTopFixes(response.data.top_fixes || []);
      }
    } catch (err) {
      console.error('Failed to fetch top fixes:', err);
    }
  };
  
  useEffect(() => {
    fetchIssues();
    fetchSummary();
    fetchTopFixes();
  }, [fetchIssues]);
  
  // Create new issue
  const handleCreateIssue = async (e) => {
    e.preventDefault();
    
    if (!newIssue.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        ...newIssue,
        job_id: newIssue.job_id || null,
        client_id: newIssue.client_id || null,
        contractor_id: newIssue.contractor_id || null
      };
      
      const response = await axios.post(`${API}/api/live-issues`, payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.ok) {
        toast.success('Issue created');
        setShowCreateForm(false);
        setNewIssue({
          source: 'fm',
          area: 'general',
          severity: 'medium',
          title: '',
          description: '',
          expected_behaviour: '',
          actual_behaviour: '',
          impact: '',
          job_id: '',
          client_id: '',
          contractor_id: ''
        });
        fetchIssues();
        fetchSummary();
        fetchTopFixes();
      }
    } catch (err) {
      console.error('Failed to create issue:', err);
      toast.error('Failed to create issue');
    } finally {
      setCreating(false);
    }
  };
  
  // Update issue status
  const updateStatus = async (issueId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/api/live-issues/${issueId}/status`, {
        status: newStatus,
        note: ''
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.ok) {
        toast.success(`Status updated to ${newStatus}`);
        fetchIssues();
        fetchSummary();
        fetchTopFixes();
      }
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error('Failed to update status');
    }
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="live-issue-tracker-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Bug className="h-6 w-6 text-red-600" />
                Live Issue Tracker
              </h1>
              <p className="text-slate-500">Ruthless fix loop - track, triage, and resolve</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => { fetchIssues(); fetchSummary(); fetchTopFixes(); }}
                variant="outline"
                size="icon"
                className="h-10 w-10"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => setShowCreateForm(true)}
                className="bg-red-600 hover:bg-red-700 gap-2"
                data-testid="create-issue-btn"
              >
                <Plus className="h-4 w-4" />
                Log Issue
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        
        {/* Create Issue Form */}
        {showCreateForm && (
          <Card className="bg-white border-red-200 shadow-lg" data-testid="create-issue-form">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-red-800">
                  <Plus className="h-5 w-5" />
                  Log New Issue
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setShowCreateForm(false)}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateIssue} className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
                    <select
                      value={newIssue.source}
                      onChange={(e) => setNewIssue({...newIssue, source: e.target.value})}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200"
                      data-testid="issue-source-select"
                    >
                      {SOURCES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Area</label>
                    <select
                      value={newIssue.area}
                      onChange={(e) => setNewIssue({...newIssue, area: e.target.value})}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200"
                      data-testid="issue-area-select"
                    >
                      {AREAS.map(a => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Severity</label>
                    <select
                      value={newIssue.severity}
                      onChange={(e) => setNewIssue({...newIssue, severity: e.target.value})}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200"
                      data-testid="issue-severity-select"
                    >
                      {SEVERITIES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="Brief description of the issue"
                    value={newIssue.title}
                    onChange={(e) => setNewIssue({...newIssue, title: e.target.value})}
                    className="w-full"
                    required
                    data-testid="issue-title-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    placeholder="Detailed description..."
                    value={newIssue.description}
                    onChange={(e) => setNewIssue({...newIssue, description: e.target.value})}
                    rows={2}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 resize-none"
                    data-testid="issue-description-input"
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Expected Behaviour</label>
                    <textarea
                      placeholder="What should happen..."
                      value={newIssue.expected_behaviour}
                      onChange={(e) => setNewIssue({...newIssue, expected_behaviour: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Actual Behaviour</label>
                    <textarea
                      placeholder="What actually happens..."
                      value={newIssue.actual_behaviour}
                      onChange={(e) => setNewIssue({...newIssue, actual_behaviour: e.target.value})}
                      rows={2}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 resize-none"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Impact</label>
                  <Input
                    type="text"
                    placeholder="How does this impact users/operations?"
                    value={newIssue.impact}
                    onChange={(e) => setNewIssue({...newIssue, impact: e.target.value})}
                    className="w-full"
                  />
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Job ID (optional)</label>
                    <Input
                      type="text"
                      placeholder="job_xxx"
                      value={newIssue.job_id}
                      onChange={(e) => setNewIssue({...newIssue, job_id: e.target.value})}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Client ID (optional)</label>
                    <Input
                      type="text"
                      placeholder="client_xxx"
                      value={newIssue.client_id}
                      onChange={(e) => setNewIssue({...newIssue, client_id: e.target.value})}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Contractor ID (optional)</label>
                    <Input
                      type="text"
                      placeholder="contractor_xxx"
                      value={newIssue.contractor_id}
                      onChange={(e) => setNewIssue({...newIssue, contractor_id: e.target.value})}
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-3 pt-2">
                  <Button 
                    type="submit"
                    disabled={creating}
                    className="bg-red-600 hover:bg-red-700 gap-2"
                    data-testid="submit-issue-btn"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4" />
                        Create Issue
                      </>
                    )}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
        
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-red-700 mb-2">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">P0 Critical</span>
                </div>
                <div className="text-3xl font-bold text-red-900">
                  {summary.grouped?.priority_counts?.p0 || 0}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-orange-700 mb-2">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm font-medium">P1 High</span>
                </div>
                <div className="text-3xl font-bold text-orange-900">
                  {summary.grouped?.priority_counts?.p1 || 0}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-yellow-700 mb-2">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-medium">P2 Medium</span>
                </div>
                <div className="text-3xl font-bold text-yellow-900">
                  {summary.grouped?.priority_counts?.p2 || 0}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm font-medium">Fixed This Week</span>
                </div>
                <div className="text-3xl font-bold text-green-900">
                  {summary.weekly?.fixed_this_week || 0}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-slate-700 mb-2">
                  <TrendingUp className="h-4 w-4" />
                  <span className="text-sm font-medium">Net Change</span>
                </div>
                <div className={`text-3xl font-bold ${(summary.weekly?.net_change || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {summary.weekly?.net_change > 0 ? '+' : ''}{summary.weekly?.net_change || 0}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Top Fixes Panel */}
        {topFixes.length > 0 && (
          <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-800">
                <Target className="h-5 w-5" />
                Top Fix Candidates (This Week)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topFixes.map((issue, idx) => (
                  <div 
                    key={issue.issue_id} 
                    className="flex items-start gap-4 p-4 bg-white rounded-xl border border-red-100"
                  >
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-red-600 text-white font-bold rounded-full">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={PRIORITY_STYLES[issue.fix_priority] || PRIORITY_STYLES.p2}>
                          {issue.fix_priority?.toUpperCase()}
                        </Badge>
                        <Badge variant="outline">{issue.area}</Badge>
                        <span className="text-sm font-medium text-slate-800">{issue.title}</span>
                      </div>
                      {issue.impact && (
                        <p className="text-sm text-slate-500 mt-1">{issue.impact}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(issue.issue_id, 'in_progress')}
                      className="gap-1"
                    >
                      <Wrench className="h-3 w-3" />
                      Start Fix
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Filters */}
        <Card className="bg-white">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Filters:</span>
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm"
              >
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="triaged">Triaged</option>
                <option value="in_progress">In Progress</option>
                <option value="fixed">Fixed</option>
                <option value="closed">Closed</option>
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm"
              >
                <option value="">All Priorities</option>
                <option value="p0">P0 Critical</option>
                <option value="p1">P1 High</option>
                <option value="p2">P2 Medium</option>
              </select>
              <select
                value={filterArea}
                onChange={(e) => setFilterArea(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm"
              >
                <option value="">All Areas</option>
                {AREAS.map(a => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm"
              >
                <option value="">All Severities</option>
                {SEVERITIES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {(filterStatus || filterArea || filterSeverity || filterPriority) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilterStatus('');
                    setFilterArea('');
                    setFilterSeverity('');
                    setFilterPriority('');
                  }}
                  className="text-slate-500"
                >
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Issues List */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Issues ({issues.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : issues.length > 0 ? (
              <div className="space-y-3">
                {issues.map((issue) => (
                  <div 
                    key={issue.issue_id}
                    className="p-4 border border-slate-200 rounded-xl hover:border-slate-300 transition-colors"
                    data-testid={`issue-${issue.issue_id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <Badge className={PRIORITY_STYLES[issue.fix_priority] || PRIORITY_STYLES.p2}>
                            {issue.fix_priority?.toUpperCase()}
                          </Badge>
                          <Badge className={STATUS_STYLES[issue.status] || STATUS_STYLES.open}>
                            {issue.status?.replace('_', ' ')}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {issue.area}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {issue.source}
                          </Badge>
                        </div>
                        <h3 className="font-medium text-slate-900">{issue.title}</h3>
                        {issue.description && (
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{issue.description}</p>
                        )}
                        {issue.impact && (
                          <p className="text-sm text-amber-700 mt-1">
                            <strong>Impact:</strong> {issue.impact}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span>Created: {formatDate(issue.created_at)}</span>
                          {issue.created_by && <span>By: {issue.created_by}</span>}
                          {issue.job_id && <span>Job: {issue.job_id}</span>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {issue.status === 'open' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateStatus(issue.issue_id, 'triaged')}
                            className="text-xs"
                          >
                            Triage
                          </Button>
                        )}
                        {issue.status === 'triaged' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateStatus(issue.issue_id, 'in_progress')}
                            className="text-xs"
                          >
                            Start
                          </Button>
                        )}
                        {issue.status === 'in_progress' && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-xs"
                            onClick={() => updateStatus(issue.issue_id, 'fixed')}
                          >
                            Mark Fixed
                          </Button>
                        )}
                        {issue.status === 'fixed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateStatus(issue.issue_id, 'closed')}
                            className="text-xs"
                          >
                            Close
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <p className="text-slate-600">No issues found</p>
                <p className="text-sm text-slate-500">All clear, or adjust your filters</p>
              </div>
            )}
          </CardContent>
        </Card>
        
      </div>
    </div>
  );
}
